<template>
  <GuideLayout title="OE 生成模块 - 使用说明" backRoute="/oegen" :navItems="navItems">
    <div class="guide-content">

<!-- 简介 -->
<section id="intro" class="guide-intro">
  <h2>这个模块是做什么的？</h2>
  <p class="intro-text">OE 生成模块可以帮你<strong>用 AI 自动模拟填写问卷中的开放题</strong>。上传一份包含答题者数据的 Excel 文件后，系统会根据每个答题者已有的客观题作答情况，自动为每道开放题生成一段真实可信的回答。整个过程分三步：<strong>准备数据 → 创建任务并运行 → 查看/下载结果</strong>。</p>
</section>

<!-- 第一步 -->
<section id="step1" class="guide-step">
  <div class="step-header">
    <span class="step-number">01</span>
    <h2>准备数据文件</h2>
  </div>
  <p>你需要准备一份 Excel 文件（<code>.xls</code> 或 <code>.xlsx</code>），至少包含 <strong>datamap</strong> 和 <strong>rawdata</strong> 两个 sheet。下面分别说明每个 sheet 的格式要求。</p>

  <h4>sheet 1：datamap（题目定义与选项映射）</h4>
  <p>datamap 定义了问卷中每道题的题型、变量名、选项编码和标签。第一行为表头，必须包含以下列名（列顺序不限）：</p>
  <table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:13px">
    <thead>
      <tr style="background:#fafbfc">
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">列名</th>
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">说明</th>
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">示例</th>
      </tr>
    </thead>
    <tbody>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Question ID</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">题目编号（qid），每道题唯一</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q1、S2</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Type</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">题型。开放题为 <code>opentext</code> 或 <code>opentextlist</code>，其他为 single/grid/multi 等</td><td style="border:1px solid #ebeef5;padding:6px 10px">opentext、single</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Variable ID</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">变量名（vid），与 rawdata 列名对应</td><td style="border:1px solid #ebeef5;padding:6px 10px">S1、Country</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Answer Code</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">选项编码（客观题的选项值，开放题为空）</td><td style="border:1px solid #ebeef5;padding:6px 10px">1、2、3</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Answer Label</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">选项标签（与 Answer Code 对应的文本）</td><td style="border:1px solid #ebeef5;padding:6px 10px">非常满意、不满意</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Question Label</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">题目文本（AI 看到的问题描述）</td><td style="border:1px solid #ebeef5;padding:6px 10px">您对该品牌的评价？</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px"><code>Level ID</code></td><td style="border:1px solid #ebeef5;padding:6px 10px">层级标识（可选列）</td><td style="border:1px solid #ebeef5;padding:6px 10px">—</td></tr>
    </tbody>
  </table>
  <p>系统通过 <code>Type</code> 列判断哪些题是开放题：只有 <code>opentext</code> 或 <code>opentextlist</code> 类型的题目才会被 AI 生成答案。客观题（single/grid/multi 等）的作答值会通过 <code>Answer Code</code> + <code>Answer Label</code> 解码为可读文本，作为 AI 生成开放题答案的上下文。</p>

  <h4>sheet 2：rawdata（答题者作答数据）</h4>
  <p>rawdata 存放每个答题者的实际作答值。第一行为表头，每列对应一个变量名（Variable ID），每行代表一个答题者。</p>
  <ul>
    <li>表头列名必须与 datamap 中的 <code>Variable ID</code> 一致（如 <code>S1</code>、<code>Country</code> 等）。</li>
    <li>必须包含 <code>respid</code> 列（答题者编号）。</li>
    <li><strong>最后一列必须为 <code>Lastplete</code></strong>（系统据此判断该行是否为有效样本，值为 <code>1</code> 表示有效）。</li>
    <li>开放题列的单元格中，需要 AI 补写的位置填入占位语 <strong><code>严格落实</code></strong>（见下方"占位语规则"）。</li>
  </ul>

  <h4>sheet 3：follow（可选 — 追问配置）</h4>
  <p>如果希望 AI 对某些开放题自动生成<strong>追问数据</strong>，可以添加一个名为 <code>follow</code> 的 sheet，无需表头，第 1 行即为数据。该 sheet 包含两列：</p>
  <ul>
    <li><strong>A 列（必填）</strong>：列出需要追问的开放题 vid，每行一个。</li>
    <li><strong>B 列（选填）</strong>：为该 vid 填写<strong>自定义追问 prompt</strong>，用于定义追问的方向或原则。填写后内容会以“该题追问补充要求”的形式拼接到默认追问 prompt 之后，引导 AI 按指定方向追问；<strong>留空则忽略</strong>，仅 A 列 vid 生效，走默认追问逻辑。</li>
  </ul>
  <p>示例（A 列为 vid，B 列为对应的自定义追问 prompt）：</p>
  <pre style="background:#f5f7fa;padding:8px 12px;border-radius:4px;font-size:12px;color:#606266">A列   B列
S1    追问应聚焦于该答题者对产品使用场景的具体描述
S5    （留空，走默认追问逻辑）
S8    追问方向围绕价格敏感度与竞品对比</pre>
  <p>系统会在生成主答案后，为这些 vid 逐轮生成最多 3 轮追问（每轮一个追问问题 + AI 回答），每轮基于原题答案和前序追问逐轮深入。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li><code>follow</code> sheet 是<strong>可选的</strong>，不包含时不会触发追问。</li>
      <li>sheet 名必须为小写 <code>follow</code>，无表头行，第 1 行即为数据，A 列为 vid、B 列为自定义追问 prompt。</li>
      <li>B 列自定义 prompt 是<strong>可选的</strong>，留空时仅 A 列 vid 生效，走默认追问逻辑。</li>
      <li>追问数据独立存储，不影响主结果。某轮追问失败后该 vid 后续追问停止，但主结果不受影响。</li>
    </ul>
  </div>

  <h4>sheet 4：toaudio（可选 — 口播转换）</h4>
  <p>如果希望 AI 生成答案时自动转为<strong>口语化风格</strong>（适合语音播报），可以添加一个名为 <code>toaudio</code> 的 sheet，在 A 列列出需要口播转换的开放题 vid，每行一个，无需表头。例如：</p>
  <pre style="background:#f5f7fa;padding:8px 12px;border-radius:4px;font-size:12px;color:#606266">S1
S5
S8</pre>
  <p>系统会将这些 vid 的 AI 回答额外转为真人日常口语风格（非播音腔），追问答案也会同步转换。转换失败时保留原句，不影响整体流程。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li><code>toaudio</code> sheet 是<strong>可选的</strong>，不包含时不会触发口播转换。</li>
      <li>口播转换仅影响 AI 回答文本，不影响中文翻译。</li>
      <li>sheet 名必须为小写 <code>toaudio</code>，无表头行，A 列即为有效数据。</li>
    </ul>
  </div>

  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>datamap 和 rawdata 通过 <code>Variable ID</code> / 列名关联，两者必须一致。</li>
      <li>文件大小建议不超过 10MB，过大的文件可能导致上传失败。</li>
    </ul>
  </div>
</section>

<!-- 数据约束规则 -->
<section id="rules" class="guide-step">
  <div class="step-header">
    <span class="step-number">!</span>
    <h2>数据约束规则（必读）</h2>
  </div>
  <p>以下规则直接影响任务能否成功运行，请在准备数据文件时务必遵守。</p>

  <h3>规则 1：占位语"严格落实"</h3>
  <p>在 rawdata 中，<strong>只有单元格内容包含"严格落实"的开放题才会被 AI 补写</strong>。系统以此来区分"需要 AI 生成"和"已有真实作答"的单元格：</p>
  <ul>
    <li>单元格<strong>包含</strong>"严格落实"（如"严格落实相关措施……"）→ 系统<strong>会</strong>用 AI 生成的答案替换该占位语。</li>
    <li>单元格<strong>不包含</strong>"严格落实"（如已有真实作答内容、空值等）→ 系统<strong>不会</strong>覆盖，保留原值不动。</li>
  </ul>
  <div class="guide-tip guide-tip--warning">
    <div class="guide-tip-title">重要</div>
    <ul>
      <li>如果你希望某道开放题的某个答题者由 AI 生成答案，该单元格中必须包含"严格落实"这四个字。</li>
      <li>如果单元格为空或不含"严格落实"，系统认为该答题者已有真实作答，不会为其生成 AI 答案。</li>
    </ul>
  </div>

  <h3>规则 2：国家题变量名固定为 Country</h3>
  <p>如果你的问卷包含"国家"题，<strong>其 Variable ID 必须命名为 <code>Country</code></strong>。系统通过这个固定变量名来识别答题者的国家，用于：</p>
  <ul>
    <li>在 AI 提示词中声明"答题者来自 XX 国家"</li>
    <li>当问卷没有 Language 题时，按国家自动推导语言（如 Country=中国 → 简体中文）</li>
    <li>影响是否生成中文翻译（非中文语言会额外生成中文翻译）</li>
  </ul>
  <p>如果问卷不包含国家题（没有 <code>Country</code> 列），系统默认视为中国答题者。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>变量名区分大小写，必须是 <code>Country</code>（首字母大写），不能是 <code>country</code> 或 <code>COUNTRY</code>。</li>
      <li>datamap 和 rawdata 中都需要使用 <code>Country</code> 作为变量名。</li>
    </ul>
  </div>

  <h3>规则 3：语言题变量名固定为 Language</h3>
  <p>如果你的问卷包含"语言"题，<strong>其 Variable ID 必须命名为 <code>Language</code></strong>。系统通过这个固定变量名来识别答题者的作答语言，用于：</p>
  <ul>
    <li>指定 AI 用哪种语言生成答案</li>
    <li>判定是否需要生成中文翻译</li>
  </ul>
  <p>如果问卷不包含语言题（没有 <code>Language</code> 列），系统会根据 Country 推导语言；Country 也没有时默认使用简体中文。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>变量名区分大小写，必须是 <code>Language</code>（首字母大写），不能是 <code>language</code> 或 <code>LANGUAGE</code>。</li>
      <li>datamap 和 rawdata 中都需要使用 <code>Language</code> 作为变量名。</li>
    </ul>
  </div>

  <h3>规则 4：Question Label 中的变量引用</h3>
  <p>datamap 的 <code>Question Label</code> 列中可以使用引用语法 <strong><code>^f('变量名')^</code></strong>，系统会自动将其替换为该答题者在该变量上的实际作答值。例如：</p>
  <pre style="background:#f5f7fa;padding:8px 12px;border-radius:4px;font-size:12px;color:#606266">您对^f('S2TitleHide')^的评价如何？</pre>
  <p>如果某答题者在 S2TitleHide 上的作答解码后为"品牌A"，AI 看到的题目文本就是"您对品牌A的评价如何？"。这让 AI 能针对每个答题者的实际选择生成更贴切的答案。</p>
  <div class="guide-tip guide-tip--warning">
    <div class="guide-tip-title">重要</div>
    <ul>
      <li><code>^f('XXX')^</code> 中引用的变量名<strong>必须在 datamap 中存在对应的行</strong>（有 Variable ID 和 Answer Code/Answer Label 的定义），否则系统无法解码，会显示为"该对象"。</li>
      <li>该变量也<strong>必须在 rawdata 中存在对应的列</strong>（有实际作答值），否则系统取不到该答题者的值，同样会显示为"该对象"。</li>
    </ul>
  </div>

  <h3>规则 5：rawdata 中的变量名必须在 datamap 中存在</h3>
  <p>rawdata 表头中的每个变量名列，都应在 datamap 中有对应的 <code>Variable ID</code> 定义。系统需要通过 datamap 来：</p>
  <ul>
    <li>判断该变量的题型（open/single/grid 等）</li>
    <li>将客观题的数值编码解码为可读文本（如 1 → "非常满意"）</li>
    <li>识别哪些列是开放题（opentext/opentextlist）</li>
  </ul>
  <p>如果 rawdata 中有 datamap 未定义的列，该列数据会被忽略，不会出现在 AI 的上下文中。</p>

  <h3>规则 6：rawdata 最后一列必须为 Lastplete</h3>
  <p>rawdata 的最后一列列名必须为 <code>Lastplete</code>，值为 <code>1</code> 表示该行是有效样本。系统据此判断：</p>
  <ul>
    <li>值不为 <code>1</code> 或最后一列不存在 → 该行被视为无效样本（noplete），跳过不处理。</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>缺少 Lastplete 列会导致任务报错"rawdata缺少Lastplete"。</li>
    </ul>
  </div>

  <h3>规则 7：题型要求</h3>
  <p>datamap 中必须至少有一行的 <code>Type</code> 为 <code>opentext</code> 或 <code>opentextlist</code>，否则任务会因"未找到开放题题型"而失败。</p>
</section>

<!-- 第二步 -->
<section id="step2" class="guide-step">
  <div class="step-header">
    <span class="step-number">02</span>
    <h2>创建任务</h2>
  </div>
  <ol>
    <li>打开"OE 生成"页面，你会看到任务列表（首次使用是空的）。</li>
    <li>点击左上角的<strong>"创建任务"</strong>按钮。</li>
    <li>在弹出的对话框中，输入一个<strong>任务名称</strong>（方便后续识别，建议取一个有意义的名字，如"XX项目_第1批"）。</li>
    <li>点击<strong>"选择文件"</strong>，选择你准备好的 Excel 数据文件。</li>
    <li>确认无误后，点击<strong>"确定"</strong>。</li>
  </ol>
  <p>创建成功后，任务会出现在列表中，显示任务名称、文件名和创建时间。</p>

  <div class="guide-tip">
    <div class="guide-tip-title">小提示</div>
    <ul>
      <li>任务名称起得好，后面找起来方便。如果名称不满意，可以点击"编辑"随时修改。</li>
      <li>每个任务对应一个独立的 Excel 文件。如果需要更换文件，在编辑任务时点击"更换文件"即可。</li>
    </ul>
  </div>
</section>

<!-- 第三步 -->
<section id="step3" class="guide-step">
  <div class="step-header">
    <span class="step-number">03</span>
    <h2>进入任务详情，配置参数</h2>
  </div>
  <p>在任务列表中，点击某条任务右侧的<strong>"查看"</strong>按钮，进入任务详情页。你会看到三个区域：</p>

  <h3>区域一：任务信息</h3>
  <p>这里展示了任务的基本信息，包括任务名称、数据文件、创建/开始/结束时间和状态。状态含义如下：</p>
  <ul>
    <li><strong>待运行</strong>（灰色）— 任务已创建，尚未点击运行（无开始时间）</li>
    <li><strong>运行中</strong>（橙色）— 任务正在执行（已有开始时间）</li>
    <li><strong>已完成</strong>（绿色）— 任务已成功完成，所有待生成单元格已处理完毕</li>
    <li><strong>运行失败</strong>（红色）— 任务因错误中断，进度条下方会显示具体错误信息（如 AI 服务商欠费/限流、数据文件解析失败等）</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">关于进度条</div>
    <ul>
      <li>任务运行中，进度条会<strong>每秒自动刷新</strong>，显示"已完成 X / Y 个单元格"和百分比。</li>
      <li>任务<strong>成功完成</strong>后，进度条自动消失，结果区域自动加载数据。</li>
      <li>任务<strong>运行失败</strong>时，进度条会保留显示并变为红色异常状态，下方红字提示错误原因，方便排查问题。</li>
      <li>刷新或重新打开详情页，如果任务仍在运行，会自动恢复进度条显示并继续轮询。</li>
    </ul>
  </div>

  <h3>区域二：运行控制（重点）</h3>
  <p>这是你配置 AI 生成参数的地方，有三个设置项：</p>

  <h4>① 选择模型</h4>
  <p>从下拉列表中选择一个 AI 模型。模型按供应商分组显示，支持搜索过滤。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>如果不确定选哪个，保持默认即可。</li>
      <li>不同模型的生成质量和速度可能不同，可以根据实际效果尝试切换。</li>
    </ul>
  </div>

  <h4>② 设置回答字数</h4>
  <p>点击"回答字数"区域进入编辑状态，输入你希望 AI 生成答案的字数要求，例如 <code>每题 20-30 字</code>。输入完成后按 <strong>Enter</strong> 或点击空白处即可自动保存。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>如果留空，系统默认使用"每题 20-30 字"。</li>
      <li>字数要求会直接影响生成答案的长度，请根据问卷的实际需要设置。</li>
      <li>输入后焦点离开即可保存，无需额外操作。</li>
    </ul>
  </div>

  <h4>③ User Prompt（高级选项，可选）</h4>
  <p>默认情况下你<strong>不需要</strong>修改 User Prompt，系统会使用内置的优化模板。如果你有特殊需求，可以点击"User Prompt"折叠区域展开编辑器，自定义提示词模板。</p>
 
 

  <h4>前后题逻辑的题号规则</h4>
  <p>需要添加前后题逻辑的，其题号规则如下：</p>
  <table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:13px">
    <thead>
      <tr style="background:#fafbfc">
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">题型</th>
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">id 规则（题号）</th>
        <th style="border:1px solid #ebeef5;padding:6px 10px;text-align:left;color:#303133">示例</th>
      </tr>
    </thead>
    <tbody>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">multi / ranking / numericlist 非 loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">qid</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q3</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">multi / ranking / numericlist loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">qid#loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q3#1</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">single / 开放题 非 loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">vid（= qid）</td><td style="border:1px solid #ebeef5;padding:6px 10px">S1</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">grid</td><td style="border:1px solid #ebeef5;padding:6px 10px">vid</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q5_1</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">single-loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">vid</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q5_1</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">开放题 loop</td><td style="border:1px solid #ebeef5;padding:6px 10px">vid</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q17_1</td></tr>
      <tr><td style="border:1px solid #ebeef5;padding:6px 10px">opentextlist 同 qid 多子 vid</td><td style="border:1px solid #ebeef5;padding:6px 10px">各自的 vid</td><td style="border:1px solid #ebeef5;padding:6px 10px">Q16_1 / Q16_2</td></tr>
    </tbody>
  </table>
  <div class="guide-tip">
    <div class="guide-tip-title">提示</div>
    <ul>
      <li>在 datamap 的 <code>Question Label</code> 中也可以添加前后题规则，题号规则同上。</li>
    </ul>
  </div>
</section>

<!-- 第四步 -->
<section id="step4" class="guide-step">
  <div class="step-header">
    <span class="step-number">04</span>
    <h2>运行任务</h2>
  </div>
  <ol>
    <li>确认模型、字数、Prompt 都设置好后，点击<strong>"运行任务"</strong>按钮。</li>
    <li>系统会提示"任务已启动"，随后页面出现进度条。</li>
    <li>进度条会<strong>每秒自动刷新</strong>，显示已完成数量和百分比，你可以实时观察生成进度。</li>
    <li>任务<strong>成功完成</strong>后，进度条自动消失，下方"生成结果"区域会自动加载数据。</li>
    <li>如果任务<strong>运行失败</strong>，进度条会变红并保留显示，下方红字提示错误原因，页面会弹出错误提示框。</li>
  </ol>

  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>任务运行中按钮会显示为"运行中..."且<strong>不可重复点击</strong>，避免多任务并发冲突。</li>
      <li>运行过程中关闭页面不影响后台任务执行，重新打开详情页后会自动恢复进度显示并继续轮询。</li>
      <li>任务运行中，<strong>"全部清除"按钮会被禁用</strong>，需要等任务完成或失败后才能清除。</li>
    </ul>
  </div>

  <h3>任务失败后如何重新运行</h3>
  <p>任务因错误中断（状态变为"运行失败"）后，可以按以下步骤重新运行：</p>
  <ol>
    <li>点击<strong>"全部清除"</strong>按钮，清空所有生成结果并重置任务状态为"待运行"。</li>
    <li>排查并修复失败原因（如更换 AI 模型、检查数据文件格式、确认 AI 服务商额度等）。</li>
    <li>重新点击<strong>"运行任务"</strong>。</li>
  </ol>
  <div class="guide-tip">
    <div class="guide-tip-title">断点续跑</div>
    <ul>
      <li>如果任务只是中途失败（如网络波动），<strong>不清除</strong>直接重新点击"运行任务"也可以：系统会跳过已成功生成的单元格，只补生成未完成的部分。</li>
      <li>但如果是 AI 服务商欠费/限流等问题，建议先清除再重新运行，确保数据完整。</li>
    </ul>
  </div>
</section>

<!-- 第五步 -->
<section id="step5" class="guide-step">
  <div class="step-header">
    <span class="step-number">05</span>
    <h2>查看生成结果</h2>
  </div>
  <p>任务完成后，页面下方的"生成结果"表格会自动加载数据。表格中每一行代表一个答题者的一道开放题的 AI 生成答案，包含以下信息：</p>
  <ul>
    <li><strong>题号</strong>（vid）— 对应的开放题编号</li>
    <li><strong>样本行</strong>（respid）— 对应的答题者编号</li>
    <li><strong>语言</strong> — 该答题者的语言</li>
    <li><strong>AI 生成内容</strong> — AI 生成的答案原文</li>
    <li><strong>翻译文本</strong> — 如果答题者语言不是中文，这里会显示答案的中文翻译</li>
    <li><strong>状态</strong> — 绿色"成功"表示正常生成，红色"失败"表示该条生成出错（如 AI 返回格式异常、答案缺失等）。<strong>单条失败不会中断整个任务</strong>，任务会继续处理其他单元格。</li>
    <li><strong>生成时间</strong> — 答案生成的时间</li>
  </ul>

  <div class="guide-tip">
    <div class="guide-tip-title">单条失败 vs 任务失败</div>
    <ul>
      <li><strong>单条失败</strong>（结果行状态为"失败"）：某个答题者的某道题生成出错，任务继续执行其他单元格。可点击"下载检查"查看 error sheet 中的错误详情。</li>
      <li><strong>任务失败</strong>（任务状态为"运行失败"）：AI 服务商级错误（如欠费、限流、网络故障）或数据文件解析失败，整个任务停止执行。需清除后重新运行。</li>
    </ul>
  </div>

  <h3>仅显示失败记录</h3>
  <p>工具栏上有一个<strong>"仅显示失败"</strong>筛选按钮。点击后表格只显示状态为"失败"的记录，方便快速定位问题。再次点击恢复显示全部记录。按钮旁还会显示当前失败条数。</p>
  <p>点击状态列的表头还可以排序，<strong>失败记录会排在前面</strong>，便于优先查看。</p>

  <h3>追问数据（展开查看）</h3>
  <p>如果某道题生成了追问数据，该行的题号旁会显示一个<strong>橙色小徽章</strong>，标注追问条数。点击该行左侧的 <strong>展开箭头</strong>，可以查看完整的追问记录。</p>
  <p>追问数据以子表格的形式展示，每条记录包含以下内容：</p>
  <ul>
    <li><strong>轮次</strong> — 追问的轮次编号（第 1 轮、第 2 轮……）</li>
    <li><strong>追问问题</strong> — AI 提出的追问问题</li>
    <li><strong>AI 回答</strong> — 对追问问题的回答</li>
    <li><strong>中文翻译</strong> — 非中文回答的翻译</li>
    <li><strong>状态</strong> — 成功/失败</li>
    <li><strong>生成时间</strong> — 追问生成的时间</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">小提示</div>
    <ul>
      <li>有追问数据的行会有橙色背景高亮，方便快速识别。</li>
      <li>每道题的追问数据是独立生成的，不影响主结果。</li>
    </ul>
  </div>

  <p>结果较多时会自动分页，每页显示 20 条。切换页码后追问数据仍然可以正常展开查看。</p>

  <h3>编辑与删除结果行</h3>
  <p>结果表格每一行右侧都有两个操作按钮：</p>
  <ul>
    <li><strong>编辑</strong><i class="el-icon-edit" style="color:#409EFF;margin:0 2px"></i> — 点击后弹出对话框，直接修改 AI 生成内容，修改后点击"保存修改"即可更新到数据库。</li>
    <li><strong>删除</strong><i class="el-icon-delete" style="color:#F56C6C;margin:0 2px"></i> — 删除该行结果。如果该行有追问数据，会一并删除并提示追问条数。</li>
  </ul>

  <h4>追问子表格的编辑与删除</h4>
  <p>展开追问数据后，每条追问记录末尾也有操作按钮：</p>
  <ul>
    <li><strong>编辑</strong> — 同时修改追问问题（follow_question）和 AI 回答（aitxt）。</li>
    <li><strong>删除</strong> — 删除单条追问记录，不影响主结果和其他追问。</li>
  </ul>

  <h3>手动刷新结果</h3>
  <p>工具栏上有<strong>"刷新"</strong>按钮，点击后可重新从数据库加载最新的结果数据。如果其他操作（如导入、编辑）后想确认数据已更新，点击刷新即可。</p>
</section>

<!-- 第六步 -->
<section id="step6" class="guide-step">
  <div class="step-header">
    <span class="step-number">06</span>
    <h2>筛选、下载与清除</h2>
  </div>

  <h4>按题号筛选</h4>
  <p>如果你只想看某道题的生成结果，在右上角的"按题号 qid 过滤"输入框中输入题号，点击"查询"或按回车即可筛选。</p>

  <h4>下载结果</h4>
  <p>点击<strong>"下载检查"</strong>按钮，系统会生成一个 <code>.xlsx</code> 文件并自动下载，<strong>文件名自动取任务名称</strong>，方便识别。这个文件包含两个 sheet：</p>
  <ul>
    <li><strong>oe</strong> — 所有成功生成的答案</li>
    <li><strong>error</strong> — 生成失败的记录（如果有的话）</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">小提示</div>
    <ul>
      <li>下载后请检查 error sheet，如果有失败记录，可以根据错误信息排查原因。</li>
      <li>常见失败原因包括：答题情况数据缺失、AI 返回格式异常、网络超时等。</li>
      <li>如果任务名称包含特殊字符（<code>\ / : * ? " &lt; &gt; |</code>），系统会自动替换为下划线，确保文件可以正常保存。</li>
    </ul>
  </div>

  <h4>导入检查（批量更新结果）</h4>
  <p>如果你想在本地修改 AI 生成内容后批量导回系统，可以按以下步骤操作：</p>
  <ol>
    <li>先点击<strong>"下载检查"</strong>下载结果 Excel。</li>
    <li>在本地 Excel 中修改需要调整的<strong>原句</strong>（主答案行）或<strong>追问问题 + 原句</strong>（追问行）。可以删除不需要修改的行，系统只处理 Excel 中存在的行。</li>
    <li>点击<strong>"导入检查"</strong>按钮，选择修改后的 Excel 文件上传。</li>
  </ol>
  <p>系统会自动按任务、样本行号和题号匹配更新数据库，并在页面上显示汇总信息：</p>
  <ul>
    <li><strong>原句更新</strong> — 主答案更新了多少条</li>
    <li><strong>追问更新</strong> — 追问数据更新了多少条</li>
    <li><strong>跳过</strong> — 因无匹配记录而跳过的行数</li>
    <li><strong>错误</strong> — 导入过程中出现的错误条数（如有）</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>导入仅做<strong>更新</strong>操作，不会新增或删除数据库记录。</li>
      <li>导入后结果列表会自动刷新，显示最新的数据。</li>
    </ul>
  </div>

  <h4>贴入句子并下载 rawdata</h4>
  <p>点击<strong>"贴入句子并下载rawdata"</strong>按钮，系统会将 AI 生成的主答案（原句）回填到原始 rawdata 表对应的答题者单元格中，生成一个新的 Excel 文件并下载。文件名格式为 <code>{任务名称}rawdata.xlsx</code>。</p>
  <p>这个文件包含两个 sheet：</p>
  <ul>
    <li><strong>rawdata</strong> — 原始数据表中，对应单元格已填入 AI 生成的原句。</li>
    <li><strong>error</strong> — 如果某些单元格不含"严格落实"占位语，说明该条未被成功贴入，会记录到错误 sheet 供排查。</li>
  </ul>
  <div class="guide-tip">
    <div class="guide-tip-title">小提示</div>
    <ul>
      <li>此功能适用于将 AI 生成的答案<strong>整合回原始数据表</strong>，方便后续导出使用。</li>
      <li>如果文件名包含特殊字符会被自动替换为下划线。</li>
    </ul>
  </div>

  <h4>全部清除</h4>
  <p>如果你想重新生成所有答案，或者任务运行失败后需要重新开始，可以点击<strong>"全部清除"</strong>按钮。这会删除该任务的所有生成结果（<strong>包括追问数据</strong>），并将任务状态重置为"待运行"（清空开始时间、结束时间、错误信息、进度）。</p>
  <div class="guide-tip guide-tip--warning">
    <div class="guide-tip-title">请谨慎操作</div>
    <ul>
      <li>"全部清除"<strong>不可恢复</strong>，清除后无法找回之前的生成结果。</li>
      <li>建议先点击"下载检查"保存结果，确认无误后再清除。</li>
      <li>任务<strong>运行中</strong>时清除按钮会被禁用，需等任务完成或失败后才能操作。</li>
      <li>清除后任务回到"待运行"状态，可以重新配置参数并点击"运行任务"。</li>
    </ul>
  </div>
</section>

<!-- 补充操作 -->
<section id="extra" class="guide-step">
  <div class="step-header">
    <span class="step-number">+</span>
    <h2>编辑与删除任务</h2>
  </div>

  <h4>编辑任务</h4>
  <p>在任务列表中点击"编辑"按钮，可以修改任务名称或更换数据文件。如果不需要更换文件，只改名称即可。</p>
  <div class="guide-tip">
    <div class="guide-tip-title">注意</div>
    <ul>
      <li>如果<strong>更换了数据文件</strong>，系统会自动清空该任务的所有生成结果并重置任务状态为"待运行"，避免新旧数据错位。</li>
      <li>只修改任务名称不会影响已有的生成结果和任务状态。</li>
    </ul>
  </div>

  <h4>删除任务</h4>
  <p>在任务列表中点击"删除"按钮，确认后任务及其关联的数据文件将被<strong>永久删除</strong>。</p>
  <div class="guide-tip guide-tip--warning">
    <div class="guide-tip-title">请谨慎操作</div>
    <ul>
      <li>删除任务会同时删除数据文件和所有生成结果，<strong>不可恢复</strong>。</li>
      <li>如果只是想重新生成，使用"全部清除"即可，不需要删除整个任务。</li>
    </ul>
  </div>
</section>

    </div>
  </GuideLayout>
</template>

<script>
import GuideLayout from "./GuideLayout.vue";

export default {
  name: "OeGenGuide",
  components: {
    GuideLayout,
  },
  data() {
    return {
      navItems: [
        { id: "intro", num: "00", text: "模块概述" },
        { id: "step1", num: "01", text: "准备数据文件" },
        { id: "rules", num: "!", text: "数据约束规则" },
        { id: "step2", num: "02", text: "创建任务" },
        { id: "step3", num: "03", text: "配置参数" },
        { id: "step4", num: "04", text: "运行任务" },
        { id: "step5", num: "05", text: "查看生成结果" },
        { id: "step6", num: "06", text: "筛选、下载与清除" },
        { id: "extra", num: "+", text: "编辑与删除任务" },
      ],
    };
  },
};
</script>

<style scoped>
/* ===== 内容区基础样式 ===== */
.guide-content {
  font-size: 14px;
  line-height: 1.8;
  color: #606266;
}

/* ===== 简介区 ===== */
.guide-intro {
  margin-bottom: 36px;
  padding-bottom: 28px;
  border-bottom: 1px solid #ebeef5;
  scroll-margin-top: 80px;
}

.guide-intro h2 {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px 0;
  letter-spacing: 0.2px;
}

.intro-text {
  font-size: 14px;
  line-height: 1.8;
  color: #606266;
}

.intro-text strong {
  color: #409eff;
  font-weight: 600;
}

/* ===== 步骤区 ===== */
.guide-step {
  margin-bottom: 36px;
  padding-bottom: 28px;
  border-bottom: 1px solid #ebeef5;
  scroll-margin-top: 80px;
}

.guide-step:last-of-type {
  border-bottom: none;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #409eff 0%, #337ecc 100%);
  color: #ffffff;
  font-family: "JetBrains Mono", "Consolas", Monaco, monospace;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
}

/* "!" 编号用红色警示样式 */
#rules .step-number {
  background: linear-gradient(135deg, #e6a23c 0%, #cf9236 100%);
  box-shadow: 0 2px 6px rgba(230, 162, 60, 0.3);
}

.step-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  letter-spacing: 0.2px;
}

.guide-step h3 {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 22px 0 10px 0;
}

.guide-step h4 {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 16px 0 8px 0;
}

.guide-step p {
  margin: 10px 0;
}

.guide-step ul,
.guide-step ol {
  margin: 10px 0;
  padding-left: 22px;
}

.guide-step li {
  margin: 6px 0;
  line-height: 1.7;
}

/* ===== 代码样式 ===== */
.guide-content code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: "JetBrains Mono", "Consolas", Monaco, monospace;
  font-size: 12.5px;
  color: #409eff;
  border: 1px solid #ebeef5;
}

/* ===== 强调文本 ===== */
.guide-content strong {
  font-weight: 600;
  color: #303133;
}

/* ===== 提示框 ===== */
.guide-tip {
  margin: 14px 0;
  padding: 12px 16px;
  background: rgba(64, 158, 255, 0.06);
  border-left: 3px solid #409eff;
  border-radius: 0 4px 4px 0;
}

.guide-tip-title {
  font-size: 13px;
  font-weight: 600;
  color: #409eff;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

.guide-tip ul {
  margin: 0;
  padding-left: 18px;
}

.guide-tip li {
  font-size: 13px;
  color: #606266;
  margin: 4px 0;
  line-height: 1.6;
}

.guide-tip--warning {
  background: rgba(230, 162, 60, 0.08);
  border-left-color: #e6a23c;
}

.guide-tip--warning .guide-tip-title {
  color: #cf9236;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .guide-content {
    font-size: 13px;
  }

  .guide-intro h2,
  .step-header h2 {
    font-size: 16px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 13px;
    border-radius: 5px;
  }
}
</style>
